import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import "./SelectCriteriaInternalExpert.css";

export default function SelectCriteriaInternalExpert({ locale = "kz" }) {
  /* ================= i18n ================= */
  const t = useMemo(
    () =>
      ({
        kz: {
          applications: "Өтінімдер",
          send: "Сараптамаға жіберу",
          sending: "Жіберілуде…",
          loading: "Жүктелуде...",
          confirmSend:
            "Сіз оқу басылымын сараптамаға жіберетініңізге сенімдісіз бе?",
          sentSuccess: "Оқу басылымы сараптамаға жіберілді!",
          sendErrorConsole: "Сараптамаға жіберуде қателік орын алды",
          genericError: "Қате орын алды.",
          searchPlaceholder: "Іздеу: баспа, атауы, пән, сынып…",
          tableHeaders: {
            publisher: "Баспа",
            title: "Оқу басылымы",
            subject: "Пән",
            grade: "Сынып",
            language: "Әзірлеу тілі",
            category: "Санат",
            action: "Әрекет",
          },
          noResults: "Ештеңе табылмады",
        },
        ru: {
          applications: "Заявки",
          send: "Отправить на экспертизу",
          sending: "Отправляем…",
          loading: "Загрузка...",
          confirmSend:
            "Вы уверены что хотите отправить учебное издание на экспертизу?",
          sentSuccess: "Учебное издание отправлено на экспертизу!",
          sendErrorConsole: "Ошибка при отправке на экспертизу:",
          genericError: "Произошла ошибка.",
          searchPlaceholder: "Поиск: издательство, название, предмет, класс…",
          tableHeaders: {
            publisher: "Издательство",
            title: "Учебное издание",
            subject: "Предмет",
            grade: "Класс",
            language: "Язык разработки",
            category: "Категория",
            action: "Действие",
          },
          noResults: "Ничего не найдено",
        },
      }[locale]),
    [locale]
  );

  /* ============== state ============== */
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  // loading только для конкретной заявки
  const [sendingId, setSendingId] = useState(null);

  // фильтр по таблице заявок
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);

  const token = localStorage.getItem("token");

  /* ============== effects ============== */
  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============== API ============== */
  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/expertise/internal/criterias/applications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications(res.data.applications || []);
    } catch (err) {

    } finally {
      setLoading(false);
    }
  };

  // ✅ Авто-отправка (критерии выбирает бэк)
  const sendToExpertiseAuto = async (applicationId) => {
    if (!window.confirm(t.confirmSend)) return;

    try {
      setSendingId(applicationId);

      await axios.post(
        `/expertise/internal/applications/${applicationId}/send-to-expertise`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(t.sentSuccess);
      await fetchApplications();
    } catch (err) {

      if (err.response?.data?.errors) {
        alert(JSON.stringify(err.response.data.errors, null, 2));
      } else {
        alert(t.genericError);
      }
    } finally {
      setSendingId(null);
    }
  };

  /* ============== filtering ============== */
  const filteredApplications = useMemo(() => {
    const query = debouncedQ.trim().toLowerCase();
    if (!query) return applications;

    const pick = (v) => String(v ?? "").toLowerCase();

    return applications.filter((a) =>
      [
        a.publisher,
        a.title,
        a.subject,
        a.grade,
        a.development_language,
        a.category,
      ].some((field) => pick(field).includes(query))
    );
  }, [applications, debouncedQ]);

  /* ============== UI ============== */
  if (loading) return <p className="sci-muted">{t.loading}</p>;

  return (
    <div className="sci-container">
      <div className="sci-header">
        <h2 className="sci-title">{t.applications}</h2>

        <div className="sci-search">
          <input
            className="sci-input"
            type="text"
            placeholder={t.searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button
              className="sci-btn sci-btn-ghost"
              onClick={() => setQ("")}
              aria-label="clear"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="sci-card">
        <table className="sci-table" role="table">
          <thead>
            <tr>
              <th>{t.tableHeaders.publisher}</th>
              <th>{t.tableHeaders.title}</th>
              <th>{t.tableHeaders.subject}</th>
              <th>{t.tableHeaders.grade}</th>
              <th>{t.tableHeaders.language}</th>
              <th>{t.tableHeaders.category}</th>
              <th>{t.tableHeaders.action}</th>
            </tr>
          </thead>

          <tbody>
            {filteredApplications.length === 0 && (
              <tr>
                <td className="sci-empty" colSpan={7}>
                  {t.noResults}
                </td>
              </tr>
            )}

            {filteredApplications.map((app) => {
              const isSending = sendingId === app.id;

              return (
                <tr key={app.id}>
                  <td>{app.publisher}</td>
                  <td>{app.title}</td>
                  <td>{app.subject}</td>
                  <td>{app.grade}</td>
                  <td>{app.development_language}</td>
                  <td>{app.category}</td>
                  <td>
                    <button
                      className="sci-btn sci-btn-primary"
                      onClick={() => sendToExpertiseAuto(app.id)}
                      disabled={!!sendingId}
                    >
                      {isSending ? t.sending : t.send}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== small hook for debounce ===== */
function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);

  return v;
}
